/** document-svc /document/clients — brokerage staff manage client→agent assignment. */

import type { Uuid } from "@re-platform/types";

import type { ApiClient } from "../client";

/** ClientOut — a brokerage's client row + its assigned agent. */
export interface ClientRow {
  id: Uuid;
  /** tenants.id of the owning brokerage. */
  tenant_id: Uuid;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  /** agents.id of the owning agent, or null when unassigned. */
  assigned_agent_id: Uuid | null;
}

/** UnassignedClientOut — the tenant-admin action queue, with a derived 2-hour SLA. */
export interface UnassignedClientRow {
  id: Uuid;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  created_at: string;
  /** True once the 2-hour SLA has elapsed (derived from created_at, no counter). */
  overdue: boolean;
  /** Minutes left before overdue (0 once overdue). */
  minutes_remaining: number;
}

/**
 * ClientDetailOut — the full record behind the client-detail page.
 *
 * `address_*` is READ-ONLY and inferred: doc-processor fills it from the
 * client's driver's-licence extraction (nothing ever asks them for it), and it
 * is what the representation agreement's ¶1 prints. Label it as licence-derived
 * in the UI rather than presenting it as confirmed.
 */
export interface ClientDetail extends ClientRow {
  phone_e164: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  marital_status: string | null;
  employment_status: string | null;
  created_at: string;
  co_client_first_name: string | null;
  co_client_last_name: string | null;
  co_client_email: string | null;
  co_client_phone_e164: string | null;
  co_client_relationship: string | null;
}

export const clientService = {
  /** One client's full record. Admins any in tenant; agents their own (404 otherwise). */
  get(client: ApiClient, clientId: Uuid) {
    return client.request<ClientDetail>(`/document/clients/${clientId}`);
  },

  list(
    client: ApiClient,
    params?: { assigned?: boolean; limit?: number; offset?: number },
  ) {
    return client.request<ClientRow[]>("/document/clients", { query: params });
  },

  /** Clients awaiting agent assignment, oldest (most overdue) first. */
  unassignedQueue(
    client: ApiClient,
    params?: { limit?: number; offset?: number },
  ) {
    return client.request<UnassignedClientRow[]>(
      "/document/clients/unassigned",
      { query: params },
    );
  },

  /** Assign (agentId) or clear (null) the client's agent. */
  assignAgent(client: ApiClient, clientId: Uuid, agentId: Uuid | null) {
    return client.request<ClientRow>(`/document/clients/${clientId}`, {
      method: "PATCH",
      body: { assigned_agent_id: agentId },
    });
  },
};
